import { useEffect, useState, type ReactNode } from "react";
import { stillness } from "./motion.js";

/**
 * THE COACH'S PORTRAIT (front door, 2026-09-20).
 *
 * The Cut Ink expression set — the same faceless monochrome register as the
 * rest of the art (ADR 0017) — at the top left of the conversation, with the
 * coach's own prose wrapping around it.
 *
 * AT REST it is the master pose. WHILE THE COACH IS WRITING it flips through
 * the ten authored expressions in a random order, and the moment the words
 * land it settles back onto the master. Nothing about the choice means
 * anything: the expressions are gestures, not readings, and no expression is
 * ever mapped to what the coach is about to say — that would be a face
 * claiming to know a verdict the record owns.
 *
 * The frames are built from the authored PNGs by
 * `scripts/build-coach-portraits.mjs` and served from `public/coach`.
 */

/** The ten expressions, by file name. `master` is the resting pose and is never in the cycle. */
const EXPRESSIONS: readonly string[] = [
  "01-welcome",
  "02-listening",
  "03-curious",
  "04-thinking",
  "05-explaining",
  "06-gentle-challenge",
  "07-reassurance",
  "08-pause-and-reframe",
  "09-encouragement",
  "10-breakthrough",
];

export const COACH_RESTING_POSE = "master";

/**
 * Long enough to read as a considered gesture; short enough to look alive.
 * Twice the first 700ms (Andre, 2026-09-21): at that pace a pose changed
 * before the eye had settled on the last one.
 */
const FRAME_MS = 1400;

function sourceOf(pose: string): string {
  return `/coach/${pose}.webp`;
}

/**
 * An index, without `Math.random` (banned repo-wide, and rightly: the one
 * source of chance in this product is the engine's seeded die). A UI
 * flourish is not the engine, so it takes its chance from the platform
 * instead. The modulo's bias over ten of four billion is not worth a word.
 */
function randomIndex(count: number): number {
  const values = new Uint32Array(1);
  globalThis.crypto.getRandomValues(values);
  return (values[0] ?? 0) % count;
}

/** The next pose: never the one already showing, so every flip is visible. */
export function nextPose(current: string): string {
  const others = EXPRESSIONS.filter((pose) => pose !== current);
  return others[randomIndex(others.length)] ?? EXPRESSIONS[0] ?? COACH_RESTING_POSE;
}

export function CoachPortrait({ writing }: { writing: boolean }): ReactNode {
  const [pose, setPose] = useState<string>(COACH_RESTING_POSE);

  // Every frame in the browser's cache before the first flip asks for one:
  // eleven small files, fetched once, so a flip is never a blank square.
  useEffect(() => {
    for (const name of [...EXPRESSIONS, COACH_RESTING_POSE]) {
      const image = new Image();
      image.src = sourceOf(name);
    }
  }, []);

  useEffect(() => {
    // Someone who has asked their machine to stop moving things gets the
    // resting pose and nothing else.
    if (!writing || stillness()) {
      setPose(COACH_RESTING_POSE);
      return;
    }
    setPose((current) => nextPose(current));
    const flip = setInterval(() => {
      setPose((current) => nextPose(current));
    }, FRAME_MS);
    return () => {
      clearInterval(flip);
      setPose(COACH_RESTING_POSE);
    };
  }, [writing]);

  // THE FRAME IS ITS OWN ELEMENT (2026-11-02): the plate and the rule around
  // the portrait, so the plate can be given a tone of its own and the image
  // left as drawn. Since the shared ink and paper (2026-11-03) the plate is
  // the exact shared paper in both places and the image is mapped to the pair
  // by the stylesheet's shared filter (design/app.css, "THE SHARED INK AND
  // PAPER FILTER"). The float, the size and the margin moved with the frame;
  // the image fills it.
  return (
    <span className="pcs-coach-frame" aria-hidden="true">
      <img
        className="pcs-coach-portrait"
        data-testid="coach-portrait"
        data-pose={pose}
        src={sourceOf(pose)}
        // Decorative: the coach speaks in the prose beside it, and a screen
        // reader announcing a new gesture every second would be noise.
        alt=""
        aria-hidden="true"
        draggable={false}
        width={384}
        height={384}
      />
    </span>
  );
}
